/**
 * @fileoverview Mock backend for the notification preferences endpoints.
 * @description Registers GET and PUT /notifications/preferences on the feature's shared mock
 * adapter, answering with the backend's success/error envelopes and the contract's error codes
 * (409/6006 not configurable, 409/6007 stale rowVersion, 400/0001 invalid body). A no-op when
 * VITE_ENABLE_MOCK_API is false, so switching to the real backend is deleting this file's one
 * import in features/notifications/index.ts.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { applyPreferenceUpdates, listPreferences } from './notification-preferences-mock-store'
import { notificationsMockAdapter } from './notifications-mock-adapter'

import type { PreferenceUpdateFailure } from './notification-preferences-mock-store'
import type { NotificationPreferenceUpdate } from '@/features/notifications/services/notification-preferences.service'

const FAILURES: Record<
  PreferenceUpdateFailure,
  { status: number; appCode: string; message: string }
> = {
  NOT_FOUND: { status: 400, appCode: '0001', message: 'Unknown notification preference' },
  NOT_CONFIGURABLE: {
    status: 409,
    appCode: '6006',
    message: 'This notification preference cannot be changed',
  },
  STALE_VERSION: {
    status: 409,
    appCode: '6007',
    message: 'The notification preferences were changed; reload and try again',
  },
}

/**
 * @description Builds the backend's error envelope.
 * @param {number} httpStatus - The HTTP status.
 * @param {string} appCode - The backend app code.
 * @param {string} message - The message.
 * @returns {object} The error body.
 */
function errorEnvelope(httpStatus: number, appCode: string, message: string) {
  return {
    httpStatus,
    httpError: 'Error',
    appCode,
    message,
    traceId: crypto.randomUUID(),
    data: [],
  }
}

/**
 * @description Reads the PUT body's preferences array, or null when the body is invalid.
 * @param {unknown} body - The raw request body (a JSON string or object).
 * @returns {NotificationPreferenceUpdate[] | null} The updates, or null if empty/over 100/invalid.
 */
function readUpdates(body: unknown): NotificationPreferenceUpdate[] | null {
  try {
    const parsed = (typeof body === 'string' ? JSON.parse(body) : body) as {
      preferences?: NotificationPreferenceUpdate[]
    }
    const updates = parsed.preferences
    return Array.isArray(updates) && updates.length >= 1 && updates.length <= 100 ? updates : null
  } catch {
    return null
  }
}

if (notificationsMockAdapter) {
  notificationsMockAdapter.onGet('/notifications/preferences').reply(() => [
    200,
    {
      httpStatus: 200,
      message: 'Notification preferences retrieved successfully',
      traceId: crypto.randomUUID(),
      data: { preferences: listPreferences() },
    },
  ])

  notificationsMockAdapter.onPut('/notifications/preferences').reply((config) => {
    const updates = readUpdates(config.data)
    const failure = updates ? applyPreferenceUpdates(updates) : 'NOT_FOUND'
    if (failure) {
      const { status, appCode, message } = FAILURES[failure]
      return [status, errorEnvelope(status, appCode, message)]
    }
    return [
      200,
      {
        httpStatus: 200,
        message: 'Notification preferences updated successfully',
        traceId: crypto.randomUUID(),
        data: { preferences: listPreferences() },
      },
    ]
  })
}
